/**
 * RSS Feed Widget - Displays news headlines from RSS feeds
 */

import { escapeText } from '../../utils/sanitize';
import type { Widget, WidgetConfig } from './index';

export interface RSSConfig extends WidgetConfig {
  feedUrl?: string;
  proxyUrl?: string; // CORS proxy for fetching feeds
  maxItems?: number;
  refreshInterval?: number; // minutes
  scrollSpeed?: number; // pixels per second for ticker mode
  mode?: 'list' | 'ticker' | 'carousel';
  showImages?: boolean;
  showDate?: boolean;
  fontSize?: number;
  color?: string;
  titleColor?: string;
}

interface FeedItem {
  title: string;
  link: string;
  description?: string;
  pubDate?: string;
  imageUrl?: string;
}

export class RSSWidget implements Widget {
  private container: HTMLElement;
  private config: RSSConfig;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private animationId: number | null = null;
  private carouselTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private items: FeedItem[] = [];
  private currentIndex = 0;

  constructor(config: RSSConfig = {}) {
    this.config = {
      feedUrl: 'https://feeds.bbci.co.uk/news/world/rss.xml',
      maxItems: 5,
      refreshInterval: 15,
      scrollSpeed: 50,
      mode: 'list',
      showImages: false,
      showDate: true,
      fontSize: 18,
      color: '#ffffff',
      titleColor: '#10b981',
      ...config,
    };

    this.container = document.createElement('div');
    this.container.className = 'widget-rss';
  }

  render(): HTMLElement {
    this.container.style.cssText = `
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      color: ${this.config.color};
    `;

    this.container.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; opacity: 0.7;">
        Loading feed...
      </div>
    `;

    this.fetchFeed();
    this.intervalId = setInterval(
      () => this.fetchFeed(),
      (this.config.refreshInterval || 15) * 60 * 1000
    );

    return this.container;
  }

  update(): void {
    this.fetchFeed();
  }

  private async fetchFeed(): Promise<void> {
    try {
      // Use a CORS proxy if provided, otherwise try direct fetch
      const url = this.config.proxyUrl
        ? `${this.config.proxyUrl}${encodeURIComponent(this.config.feedUrl!)}`
        : this.config.feedUrl!;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch RSS feed');

      const text = await response.text();
      this.items = this.parseRSS(text);
      this.updateDisplay();
    } catch (error) {
      console.error('[RSSWidget] Failed to fetch feed:', error);
      this.container.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; opacity: 0.5;">
          Feed unavailable
        </div>
      `;
    }
  }

  private parseRSS(xml: string): FeedItem[] {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'text/xml');
      const items = doc.querySelectorAll('item');
      const result: FeedItem[] = [];

      items.forEach((item, index) => {
        if (index >= (this.config.maxItems || 5)) return;

        const title = item.querySelector('title')?.textContent || '';
        const link = item.querySelector('link')?.textContent || '';
        const description = item.querySelector('description')?.textContent || '';
        const pubDate = item.querySelector('pubDate')?.textContent || '';

        // Try to find image
        let imageUrl: string | undefined;
        const enclosure = item.querySelector('enclosure[type^="image"]');
        if (enclosure) {
          imageUrl = enclosure.getAttribute('url') || undefined;
        }
        // Also check media:thumbnail or media:content
        const mediaThumbnail = item.querySelector('thumbnail, content[medium="image"]');
        if (!imageUrl && mediaThumbnail) {
          imageUrl = mediaThumbnail.getAttribute('url') || undefined;
        }

        result.push({ title, link, description, pubDate, imageUrl });
      });

      return result;
    } catch (error) {
      console.error('[RSSWidget] Failed to parse RSS:', error);
      return [];
    }
  }

  private updateDisplay(): void {
    if (this.items.length === 0) {
      this.container.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; opacity: 0.5;">
          No items in feed
        </div>
      `;
      return;
    }

    switch (this.config.mode) {
      case 'ticker':
        this.renderTicker();
        break;
      case 'carousel':
        this.renderCarousel();
        break;
      default:
        this.renderList();
    }
  }

  private renderList(): void {
    // SECURITY: Escape all user-provided content to prevent XSS
    const html = this.items.map((item, index) => `
      <div style="
        padding: 12px 0;
        border-bottom: 1px solid rgba(255,255,255,0.1);
        ${index === 0 ? 'padding-top: 0;' : ''}
      ">
        <div style="font-size: ${this.config.fontSize}px; font-weight: 600; color: ${this.config.titleColor}; line-height: 1.3;">
          ${escapeText(item.title)}
        </div>
        ${this.config.showDate && item.pubDate ? `
          <div style="font-size: ${Math.round(this.config.fontSize! * 0.7)}px; opacity: 0.6; margin-top: 4px;">
            ${escapeText(this.formatDate(item.pubDate))}
          </div>
        ` : ''}
      </div>
    `).join('');

    this.container.innerHTML = `
      <div style="padding: 16px; overflow-y: auto; height: 100%;">
        ${html}
      </div>
    `;
  }

  private renderTicker(): void {
    // SECURITY: Escape all user-provided content to prevent XSS
    const tickerText = this.items.map(item => escapeText(item.title)).join('  •  ');

    this.container.innerHTML = `
      <div style="display: flex; align-items: center; height: 100%; overflow: hidden;">
        <div class="ticker-content" style="white-space: nowrap; font-size: ${this.config.fontSize}px; font-weight: 600;">
          ${tickerText}  •  ${tickerText}
        </div>
      </div>
    `;

    // Animate ticker
    const tickerContent = this.container.querySelector('.ticker-content') as HTMLElement;
    if (tickerContent) {
      let position = 0;
      const animate = () => {
        position -= this.config.scrollSpeed! / 60;
        if (Math.abs(position) > tickerContent.offsetWidth / 2) {
          position = 0;
        }
        tickerContent.style.transform = `translateX(${position}px)`;
        this.animationId = requestAnimationFrame(animate);
      };
      animate();
    }
  }

  private renderCarousel(): void {
    if (this.items.length === 0) return;

    const item = this.items[this.currentIndex];

    // SECURITY: Escape all user-provided content to prevent XSS
    this.container.innerHTML = `
      <div style="display: flex; flex-direction: column; justify-content: center; height: 100%; padding: 24px; text-align: center;">
        <div style="font-size: ${Math.round(this.config.fontSize! * 1.2)}px; font-weight: 700; color: ${this.config.titleColor}; line-height: 1.3;">
          ${escapeText(item.title)}
        </div>
        ${this.config.showDate && item.pubDate ? `
          <div style="font-size: ${Math.round(this.config.fontSize! * 0.7)}px; opacity: 0.6; margin-top: 12px;">
            ${escapeText(this.formatDate(item.pubDate))}
          </div>
        ` : ''}
        <div style="font-size: 12px; opacity: 0.4; margin-top: 16px;">
          ${this.currentIndex + 1} / ${this.items.length}
        </div>
      </div>
    `;

    // Auto-advance carousel (track timeout for cleanup)
    if (this.carouselTimeoutId) {
      clearTimeout(this.carouselTimeoutId);
    }
    this.carouselTimeoutId = setTimeout(() => {
      this.currentIndex = (this.currentIndex + 1) % this.items.length;
      this.renderCarousel();
    }, 5000);
  }

  private formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  }

  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.carouselTimeoutId) {
      clearTimeout(this.carouselTimeoutId);
      this.carouselTimeoutId = null;
    }
  }
}
